import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// 1. Define the variants first
const buttonVariants = cva(
  // We use CSS variables for everything that needs to change in CS16 mode
  // This prevents the need for !important in your CSS
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Default: uses standard shadcn-like colors
        // We use the variables we defined in globals.css
        default:
          "bg-[var(--btn-bg)] text-[var(--btn-text)] border-[length:var(--btn-border-width)] border-t-[var(--btn-border-top)] border-l-[var(--btn-border-left)] border-b-[var(--btn-border-bottom)] border-r-[var(--btn-border-right)] shadow-[var(--btn-shadow)] transition-[var(--btn-transition)] hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "bg-[var(--btn-outline-bg)] border-[length:var(--btn-outline-border-width)] border-t-[var(--btn-border-top)] border-l-[var(--btn-border-left)] border-b-[var(--btn-border-bottom)] border-r-[var(--btn-border-right)] shadow-[var(--btn-shadow)] transition-[var(--btn-transition)] hover:bg-primary/90",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 px-3 text-xs",
        lg: "h-10 px-8",
        icon: "h-9 w-9",
        "icon-sm": "h-7 w-7",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

// 2. Define the interface SECOND (so it can reference buttonVariants)
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

// 3. Define the component LAST (so it can reference ButtonProps)
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(
        buttonVariants({ variant, size, className }),
        "rounded-[var(--btn-radius)]", // Apply the dynamic radius here
      )}
      ref={ref}
      {...props}
    />
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };
